import React, {
  Children,
  createContext,
  isValidElement,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const RouterContext = createContext(null);
const ParamsContext = createContext({});

function normalizedPathname(value) {
  const raw = String(value || '/').split(/[?#]/, 1)[0] || '/';
  if (raw === '/') return '/';
  return `/${raw.replace(/^\/+|\/+$/g, '')}`;
}

function currentLocation() {
  if (typeof window === 'undefined') return { pathname: '/' };
  return { pathname: normalizedPathname(window.location.pathname) };
}

export function BrowserRouter({ children }) {
  const [location, setLocation] = useState(currentLocation);

  useEffect(() => {
    const onPopState = () => setLocation(currentLocation());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const navigate = useMemo(
    () => (destination, options = {}) => {
      if (typeof destination === 'number') {
        window.history.go(destination);
        return;
      }
      const next = normalizedPathname(destination);
      const method = options.replace ? 'replaceState' : 'pushState';
      window.history[method](null, '', next);
      setLocation({ pathname: next });
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    },
    []
  );

  return React.createElement(
    RouterContext.Provider,
    { value: { location, navigate } },
    children
  );
}

export function useNavigate() {
  const router = useContext(RouterContext);
  if (!router) throw new Error('useNavigate_must_be_used_inside_BrowserRouter');
  return router.navigate;
}

export function useParams() {
  return useContext(ParamsContext);
}

export function NavLink({ to, end = false, className = '', children, ...rest }) {
  const router = useContext(RouterContext);
  if (!router) throw new Error('NavLink_must_be_used_inside_BrowserRouter');
  const target = normalizedPathname(to);
  const pathname = router.location.pathname;
  const active = end
    ? pathname === target
    : target === '/' || pathname === target || pathname.startsWith(`${target}/`);
  const resolvedClassName =
    typeof className === 'function'
      ? className({ isActive: active })
      : [className, active ? 'active' : ''].filter(Boolean).join(' ');

  return React.createElement(
    'a',
    {
      ...rest,
      href: target,
      className: resolvedClassName,
      'aria-current': active ? 'page' : undefined,
      onClick(event) {
        rest.onClick?.(event);
        if (
          event.defaultPrevented ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey ||
          rest.target === '_blank'
        ) {
          return;
        }
        event.preventDefault();
        router.navigate(target);
      },
    },
    children
  );
}

export function Route() {
  return null;
}

function matchRoute(pattern, pathname, end = true) {
  const patternParts = normalizedPathname(pattern).split('/').filter(Boolean);
  const pathParts = normalizedPathname(pathname).split('/').filter(Boolean);
  if (end && patternParts.length !== pathParts.length) return null;
  if (!end && pathParts.length < patternParts.length) return null;

  const params = {};
  for (let index = 0; index < patternParts.length; index += 1) {
    const expected = patternParts[index];
    const observed = pathParts[index];
    if (expected.startsWith(':')) {
      if (observed === undefined) return null;
      params[expected.slice(1)] = decodeURIComponent(observed);
      continue;
    }
    if (expected !== observed) return null;
  }
  return params;
}

export function Routes({ children }) {
  const router = useContext(RouterContext);
  if (!router) throw new Error('Routes_must_be_used_inside_BrowserRouter');

  for (const child of Children.toArray(children)) {
    if (!isValidElement(child)) continue;
    const params = matchRoute(child.props.path, router.location.pathname, child.props.end !== false);
    if (params) {
      return React.createElement(ParamsContext.Provider, { value: params }, child.props.element);
    }
  }
  return null;
}
